import { ChangeDetectionStrategy, Component, DestroyRef, inject, input, OnChanges, OnInit, signal, SimpleChanges } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormGroup } from '@angular/forms';
import { isNumber, isString } from 'lodash';
import { Branch, BranchKeystore, FindingsFormGroup, IncidentFormControls, ReviewTemplate, ReviewTemplateType } from '../../../../../types';
import { templateFormFields } from './template-form-fields';

@Component({
  selector: 'app-review-form-template',
  templateUrl: './review-form-template.component.html',
  styleUrl: './review-form-template.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ReviewFormTemplateComponent implements OnChanges, OnInit {
  private destroyRef = inject(DestroyRef);

  public form = input.required<FindingsFormGroup>();
  public incidentForm = input.required<FormGroup<IncidentFormControls>>();
  public branch = input<Branch | undefined>(undefined);

  public readonly templateOptions = templateFormFields;
  public template = signal<ReviewTemplate>(templateFormFields[0]);

  public availableTemplates = signal<ReviewTemplate[]>([]);

  public availableTemplatesForBranch() {
    const disableNone = (this.branch()?.keystore as BranchKeystore | undefined)?.disable_none_template ?? false;
    const nextTemplates = disableNone ? this.templateOptions.filter((t) => t.template !== 'None') : this.templateOptions;

    return nextTemplates.filter((t) => {
      const isCancellation = this.incidentForm().controls.theftType?.value?.is_cancellation;
      const isUnderringing = this.incidentForm().controls.theftType?.value?.is_underringing;
      const isWalkout = this.incidentForm().controls.theftType?.value?.is_walkout;
      const isCashier = this.incidentForm().controls.theftType?.value?.is_cashier;

      if (t.template === 'None' && !disableNone && (isCancellation || isUnderringing || isWalkout || isCashier)) {
        return true;
      }

      const matchesType = (type: ReviewTemplateType) => {
        if (type === 'Cancellation' && isCancellation) return true;
        if (type === 'Underringing' && isUnderringing) return true;
        if (type === 'Walkout' && isWalkout) return true;

        return false;
      };

      return t.appliesToTypes.some(matchesType);
    });
  }

  public hasStartedWatchingFormChanges = signal(false);

  ngOnInit() {
    this.availableTemplates.set(this.availableTemplatesForBranch());

    this.incidentForm()
      .controls.theftType.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        const nextTemplates = this.availableTemplatesForBranch();
        this.availableTemplates.set(nextTemplates);
        if (!nextTemplates.some((t) => t.template === this.template()?.template)) {
          this.template.set(nextTemplates[0]);
          this.clearUnavailableSelectValues();
        }
      });
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['branch']) {
      if (this.template()?.template === 'None' && !this.availableTemplatesForBranch().some((f) => f.template === 'None')) {
        this.template.set(this.availableTemplatesForBranch()[0]);
        this.clearUnavailableSelectValues();
      }
    }
  }

  private clearUnavailableSelectValues() {
    this.template()?.fields.forEach((f) => {
      if (f.options && !f.options.includes(this.form().get(f.formControlName)?.value)) this.form().get(f.formControlName)?.setValue(null);
    });
  }

  public changeTemplate() {
    this.form().controls.howManyItemsScanned.setValue(undefined);
    this.form().controls.kioskInteractionResult.setValue(undefined);
    this.form().controls.howManyItemsScanned.markAsUntouched();
    this.form().controls.kioskInteractionResult.markAsUntouched();
    this.clearUnavailableSelectValues();

    if (this.template().template !== 'None') {
      this.form().controls.comments.setValue('');
    }
  }

  public generateCommentsFromTemplate() {
    if (this.template().template === 'None') return;

    const resultingChoices = this.template().results;
    const resultingComments = [];

    resultingChoices.forEach((choice) => {
      if (
        choice.keys.some((k) => {
          const control = this.form().get(k);

          if (isString(control.value) && !control.value) return true;

          if (control.value) return false;
          if (isNumber(control.value)) return false;

          return true;
        })
      ) {
        return;
      }

      let formatted = choice.template;
      choice.keys.forEach((k) => {
        const value = this.form().get(k).value;
        formatted = formatted.split(`{${k}}`).join(value.toString().toLowerCase());
      });

      resultingComments.push(formatted);
    });

    const finalComments = resultingComments.join(' ');
    this.form().controls.comments.setValue(finalComments);
  }
}
